import { FormControl, FormControlLabel, FormLabel, RadioGroup, FormHelperText, SxProps, Theme } from '@mui/material';
import Radio from '../../inputs/Radio';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';

type RadioOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

export const FormikRadioGroup = <T extends Record<string, unknown>>({
  id,
  label,
  options,
  row = false,
  testId,
  sx,
  ...rest
}: BaseFormikFieldProps<T> & {
  options: RadioOption[];
  row?: boolean;
  testId?: string;
  sx?: SxProps<Theme>;
}) => {
  const { [TID_ATTR]: restTestId, ...restWithoutTestId } = rest as Record<string, unknown> & { [TID_ATTR]?: string };
  const baseTestId = testId ?? restTestId;
  const { status, handleChange, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  const labelTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:label` } : {};
  const groupTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:group` } : {};
  const helperTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:helper-text` } : {};

  return (
    <FormControl error={hasError} component="fieldset" fullWidth>
      {label && (
        <FormLabel component="legend" {...labelTestId}>
          {label}
        </FormLabel>
      )}
      <RadioGroup
        row={row}
        name={String(id)}
        value={fieldValue ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ flexWrap: 'nowrap', whiteSpace: 'nowrap', mb: '12px', ...sx }}
        {...groupTestId}
        {...restWithoutTestId}
      >
        {options.map((option) => (
          <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} disabled={option.disabled} />
        ))}
      </RadioGroup>
      {hasError && errorText && (
        <FormHelperText error {...helperTestId}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
